import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BlogPostShell, POSTS } from "@/pages/Blog";

const POST = POSTS.find((p) => p.slug === "what-to-look-for-in-camera-club-competition-software");
const OG = "https://cameraclubscore.com/og/blog-generic.png";
const URL = "https://cameraclubscore.com/blog/what-to-look-for-in-camera-club-competition-software";

export default function PostSoftware() {
  return (
    <>
      <Helmet>
        <title>{`${POST.title} — Camera Club Score`}</title>
        <meta name="description" content={POST.excerpt} />
        <link rel="canonical" href={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <meta property="og:title" content={POST.title} />
        <meta property="og:description" content={POST.excerpt} />
        <meta property="og:image" content={OG} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG} />
      </Helmet>
      <BlogPostShell post={POST}>
        <p className="lead">
          I built Camera Club Score because I couldn't find a tool that
          did what our committee actually needed. That process taught me a
          lot about what genuinely matters when you're picking software for
          a camera club — and what marketing puffery to ignore.
        </p>

        <h2>The ten questions to ask any tool</h2>

        <h3>1. Does it work on our devices?</h3>
        <p>
          Windows-only or Mac-only tools are increasingly a red flag. Web
          apps that work in any browser on any device (phone, tablet,
          laptop) are the future — and the present, really.
        </p>

        <h3>2. Can multiple users access it?</h3>
        <p>
          The Competition Secretary shouldn't be a single point of failure.
          Every serious tool now supports multiple logins with different
          roles (Admin, Secretary, Viewer). If a tool ships with one
          login for the whole club, keep looking.
        </p>

        <h3>3. Can it record photographers and image titles?</h3>
        <p>
          Table stakes. If it can't, walk away.
        </p>

        <h3>4. Can it print score sheets?</h3>
        <p>
          Print-ready score sheets and judging reports are non-negotiable
          for record-keeping. Camera Club Score generates PDFs from every
          Results and Season Stats page.
        </p>

        <h3>5. Can I export competition results?</h3>
        <p>
          If your data is trapped inside the tool, you're on a leash. Every
          screen should export to CSV. Camera Club Score exports
          photographers, results, and certificates — all as CSV, no
          questions asked.
        </p>

        <h3>6. Can it import member lists?</h3>
        <p>
          You already have your photographer list somewhere. Any tool that
          makes you retype it is disqualified. Bulk CSV import should be a
          one-click affair.
        </p>

        <h3>7. Can it manage judges' scores efficiently?</h3>
        <p>
          Live score entry with a keyboard-driven grid is table stakes.
          Enter/Tab/Arrow-key navigation, auto-save, offline queueing.
          Anything less and your Secretary is going to hate you.
        </p>

        <h3>8. Does it calculate league tables automatically?</h3>
        <p>
          Purpose-built tools handle sum-of-all, best-N, and PoY points
          configurations. If you're still writing formulas in Excel to
          get your season league, that's a sign you've outgrown Excel.
        </p>

        <h3>9. Can I back up my data?</h3>
        <p>
          One-click backup / restore is essential for committee handovers.
          If the current secretary retires, the next one shouldn't need a
          PhD to find last year's results. Camera Club Score has a
          Backup/Restore section in Settings.
        </p>

        <h3>10. What's the pricing model?</h3>
        <p>
          Beware "free trial then £500 per year" pricing that assumes
          you'll pay through inertia. Look for transparent per-year pricing
          you can compare to alternatives. Camera Club Score is currently
          free for all clubs, with modest paid tiers coming in the future
          — existing clubs will stay free forever as an early-adopter
          reward.
        </p>

        <h2>The honest recommendation</h2>
        <p>
          Try two or three tools with your next competition. Bring a
          Committee member to help judge. Whichever one your Secretary
          least wants to give up is the right one for your club.
        </p>
        <p>
          If Camera Club Score wins that comparison, we'd love to have
          you. <Link to="/register">Sign up free</Link>. If a different
          tool wins, we still built you a better shortlist.
        </p>

        <p><em>— Mick, founder of Camera Club Score</em></p>
      </BlogPostShell>
    </>
  );
}
